import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { compose, branch } from 'recompose'
import propsChangeCheck from 'helpers/propsChangeCheck'
import AutoSizer from 'react-virtualized-auto-sizer'

import Table from './Table'
import ContainerHeader from './ContainerHeader'

import remoteDataHOC from '../../DataTable/wrappers/withRemoteData'
import filterFormHOC from '../../DataTable/wrappers/withFilterForm'
import addIconHOC from '../wrappers/withAddIcon'
import mutateRowHOC from '../wrappers/withMutateRow'

import useTable from '../hooks/useTable'
import useLocale from 'hooks/useLocale'

import tableStyle from '../../DataTable/DataTable.module.scss'

export const DataTableContext = React.createContext({})

const DataTable = ({ tableRef, ...props }) => {
	const {
		label,
		FilterBtn,
		FilterWindow,
		loading,
		height,
		width,
		rowHeight = 36,
		withSearch,
		withFooter,
		withColumnFilter,
		onSelect,
		isSelected,
		columns,
		error,
		hasNextPage,
		hasPreviousPage,
		loadNextPage,
		loadPreviousPage,
		skipPageReset,
		updateMyData,
		tableActions,
		withSelectRow
	} = props

	const locales = useLocale()
	const formattedCols = useMemo(() => columns?.map(column => ({
		Cell: ({ value = null }) => value,
		...column,
		// Columns can have 'format' method which accepts some options. One of them is 'locales' which will be used in cell values.
		// 'format' usually returns object with 'Cell' method
		...(typeof column.format === 'function') && { ...column.format({ locales }) }
	})), [columns])
	const tableProps = useTable({ ...props, columns: formattedCols, autoResetPage: !skipPageReset })
	const { getTableProps, allColumns, rows } = tableProps

	const [leftoverWidth, visibleColNum] = allColumns?.reduce((acc, { width: colWidth, isVisible }) => {
		return isVisible ? [acc[0] - colWidth, acc[1] + 1] : acc
	}, [width, 0])

	let scrollWidth = 0
	if (height || (rows.length >= 20)) {
		let bodyHeight
		if (height) {
			bodyHeight = height - rowHeight
		} else {
			bodyHeight = rows.length * rowHeight - rowHeight
		}
		if (withFooter) {
			bodyHeight -= 36
		}
		if (withSearch) {
			bodyHeight -= 36
		}
		if ((bodyHeight < (rows.length * 36))) {
			scrollWidth = 15
		}
	}

	const additionalWidth = leftoverWidth > 0 ? Math.floor((leftoverWidth - scrollWidth) / visibleColNum) : 0

	const calculateHeight = (tableRows) => {
		let out = 108 // one row with no vertical scroll
		if (tableRows > 1) {
			out = rowHeight * (tableRows + 1) + 36	// total rows height + header height
		}
		if (tableRows > 20) {
			out = rowHeight * 21 // max height 20 rows if no height parametr
		}
		if (withSearch) {
			out += 40
		}
		if (withFooter) {
			out += 36
		}
		return out
	}

	const context = {
		originalColumns: columns,
		label,
		loading,
		error,
		hasNextPage,
		hasPreviousPage,
		loadNextPage,
		loadPreviousPage,
		height: height ? height : calculateHeight(rows.length),
		width,
		rowHeight,
		withSearch,
		withFooter,
		withColumnFilter,
		additionalWidth,
		updateMyData, // This is a custom function for update data in EditableCell
		tableActions,
		withSelectRow, // select Row Kostya's implementation
		onSelect, // select Row Vanya's implementation
		isSelected, // select Row Vanya's implementation
		...tableProps
	}
	if(tableRef) {
		tableRef.current = context
	}

	return(
		<>
			<DataTableContext.Provider value={context}>
				<div className={tableStyle.root}>
					<div className={tableStyle.tableBody}>
						<ContainerHeader FilterBtn={<>{FilterBtn}</>}/>
						<Table {...getTableProps()}/>
						{FilterWindow}
					</div>
				</div>
			</DataTableContext.Provider>
		</>
	)
}

const AutoWidthDataTable = ({ width, ...props }) => (
	<AutoSizer disableHeight={true} disableWidth={!!width} style={{ width: width }}>
		{({ width: autoWidth }) => (
			<DataTable width={width || autoWidth} {...props}/>
		)}
	</AutoSizer>
)

DataTable.defaultProps = {
	data: [],
	rowHeight: 36,
	groupBy: {
		columns: []
	}
}

DataTable.propTypes = {
	label: PropTypes.string,
	rowHeight: PropTypes.number,
	height: PropTypes.number,
	width: PropTypes.number,
	data: PropTypes.array.isRequired,
	columns: PropTypes.array.isRequired,
	withSearch: PropTypes.bool,
	withFooter: PropTypes.bool,
	withColumnFilter: PropTypes.bool,
	loading: PropTypes.bool,
	groupBy: PropTypes.shape({
		columns: PropTypes.arrayOf(PropTypes.string)
	}),
	sortBy: PropTypes.object,
	tableRef: PropTypes.object, // ref to the whole Table state
	skipPageReset: PropTypes.bool,
	updateMyData: PropTypes.func,
	tableActions: PropTypes.shape({
		label: PropTypes.string,
		Icon: PropTypes.object,
		iconStyles: PropTypes.string,
		modalComponent: PropTypes.object
	}),
	withSelectRow: PropTypes.bool,
	onSelect: PropTypes.func,
	isSelected: PropTypes.func,
	remoteOptions: PropTypes.shape({
		query: PropTypes.object,
		withFilter: PropTypes.shape({
			FilterComponent: PropTypes.element,
			baseFilter: PropTypes.object,
			filterValues: PropTypes.object,
			onFilter: PropTypes.func,
			onUnmount: PropTypes.func
		})
	})
}

export default compose(
	propsChangeCheck(['data', 'columns', 'match', 'remoteOptions']),
	branch(
		({ mutateRow }) => !!mutateRow,
		mutateRowHOC
	),
	branch(
		({ onAddRecord }) => !!onAddRecord,
		addIconHOC
	),
	branch(
		({ remoteOptions = {} }) => remoteOptions.withFilter && remoteOptions.withFilter.FilterComponent,
		filterFormHOC
	),
	branch(
		({ remoteOptions }) => !!remoteOptions,
		remoteDataHOC
	)
)(AutoWidthDataTable)
